// Scoring and results for a Revise run (pure, server-safe).
//
// The renderer, the tests and the migration audit all read scores from here,
// so "what does this run mark this response as?" has exactly one answer.
//
// Marks are all-or-nothing per item, as in both authors' engines. A
// multi-select scores its marks only when the whole set is right (Dylan Rule 8
// explains why partial credit is deliberately NOT emulated: it would need the
// results payload to change first). Sort and order report a fraction for the
// learner's feedback but earn marks only when complete.

import type { QuestionScreen, ReviseBand, ReviseRun, MtaScreen } from './types';
import { assessedQuestions, REVISE_RESULTS_KEY } from './types';
import { nscLevelOf } from './tags';

// ---------------------------------------------------------------------------
// Option shuffle: deterministic per (run, question) so a reload deals the same
// letters. `{optN}` in feedback resolves against the dealt order.
// ---------------------------------------------------------------------------

function hashSeed(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Display order: position → authored index. */
export function seededOrder(n: number, seed: string): number[] {
  const rnd = mulberry32(hashSeed(seed));
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  return order;
}

export const OPTION_LETTERS = 'ABCDEFGH';

/** Rewrite `{optN}` (authored index N) to the letter that option was dealt. */
export function resolveOptionRefs(text: string, order: number[]): string {
  return text.replace(/\{opt(\d+)\}/g, (m, n) => {
    const pos = order.indexOf(parseInt(n, 10));
    return pos >= 0 ? OPTION_LETTERS[pos] ?? m : m;
  });
}

// ---------------------------------------------------------------------------
// Grading
// ---------------------------------------------------------------------------

/** What the learner did, in AUTHORED indices. Never letters. */
export interface QuestionResponse {
  /** mcq / multi / sentence-mark: chosen authored indices. */
  selected?: number[];
  /** order: item indices in the learner's sequence. */
  sequence?: number[];
  /** sort: item index → column index. */
  placement?: Record<string, number>;
  /** Seconds spent, when the profile times questions. */
  seconds?: number;
}

export interface Grade {
  correct: boolean;
  /** Share right, for feedback copy. 1 when correct. */
  fraction: number;
  marksEarned: number;
  marksTotal: number;
}

export function marksOf(q: QuestionScreen): number {
  return typeof q.marks === 'number' ? q.marks : 0;
}

export function gradeQuestion(q: QuestionScreen, r: QuestionResponse | undefined): Grade {
  const total = q.assessed ? marksOf(q) : 0;
  const done = (correct: boolean, fraction: number): Grade => ({
    correct,
    fraction,
    marksEarned: correct ? total : 0,
    marksTotal: total,
  });
  if (!r) return done(false, 0);

  switch (q.mode) {
    case 'mcq':
    case 'sentence-mark': {
      const chosen = r.selected?.[0];
      if (chosen === undefined) return done(false, 0);
      const acceptable = Array.isArray(q.correct) ? q.correct : [q.correct ?? 0];
      return done(acceptable.includes(chosen), acceptable.includes(chosen) ? 1 : 0);
    }
    case 'multi': {
      const want = new Set(Array.isArray(q.correct) ? q.correct : [q.correct ?? 0]);
      const got = new Set(r.selected ?? []);
      let hit = 0;
      for (const w of want) if (got.has(w)) hit++;
      const exact = hit === want.size && got.size === want.size;
      return done(exact, want.size ? hit / want.size : 0);
    }
    case 'order': {
      const want = q.sequence ?? [];
      const got = r.sequence ?? [];
      if (want.length === 0) return done(false, 0);
      let hit = 0;
      for (let i = 0; i < want.length; i++) if (got[i] === want[i]) hit++;
      return done(hit === want.length && got.length === want.length, hit / want.length);
    }
    case 'sort': {
      const want = q.placement ?? {};
      const keys = Object.keys(want);
      if (keys.length === 0) return done(false, 0);
      let hit = 0;
      for (const k of keys) if (r.placement?.[k] === want[k]) hit++;
      return done(hit === keys.length, hit / keys.length);
    }
    case 'build':
      // Never scored; any choice "completes" it.
      return { correct: true, fraction: 1, marksEarned: 0, marksTotal: 0 };
  }
}

export interface MtaResponse {
  /** criterion id → the learner's verdict. */
  verdicts: Record<string, 'yes' | 'partial' | 'no'>;
}

export function gradeMta(m: MtaScreen, r: MtaResponse | undefined): { matches: number; of: number } {
  const of = m.criteria.length;
  if (!r) return { matches: 0, of };
  let matches = 0;
  for (const c of m.criteria) if (r.verdicts[c.id] === c.correct) matches++;
  return { matches, of };
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface TagBreakdown {
  earned: number;
  total: number;
  /** Items carrying the tag. */
  n: number;
  /** Items answered correctly. */
  right: number;
}

export interface ReviseSummary {
  earned: number;
  total: number;
  /** 0–100, capped. */
  pct: number;
  answered: number;
  assessed: number;
  byTag: Record<string, TagBreakdown>;
  wrong: Array<{ id: string; short: string; tag?: string; marks: number; number?: string }>;
  /** Slowest three, paper profile only (needs `seconds` on responses). */
  slow: Array<{ id: string; short: string; seconds: number }>;
  /** DBE achievement level 1–7, NSC paper only. */
  nscLevel?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /** L1/L2/L3 split, NSC paper only. */
  levels?: Record<'L1' | 'L2' | 'L3', { earned: number; total: number }>;
  band: ReviseBand | null;
}

/** The DBE seven-level scale, as Dylan's engine computes it. */
export function dbeLevel(pct: number): 1 | 2 | 3 | 4 | 5 | 6 | 7 {
  if (pct >= 80) return 7;
  if (pct >= 70) return 6;
  if (pct >= 60) return 5;
  if (pct >= 50) return 4;
  if (pct >= 40) return 3;
  if (pct >= 30) return 2;
  return 1;
}

/** The band with the highest `min` at or below `pct`; null when none match. */
export function bandFor(bands: ReviseBand[], pct: number): ReviseBand | null {
  let best: ReviseBand | null = null;
  for (const b of bands) {
    if (pct >= b.min && (best === null || b.min > best.min)) best = b;
  }
  return best;
}

export function summarise(run: ReviseRun, answers: Record<string, QuestionResponse>): ReviseSummary {
  const qs = assessedQuestions(run);
  const byTag: Record<string, TagBreakdown> = {};
  const wrong: ReviseSummary['wrong'] = [];
  const timed: Array<{ id: string; short: string; seconds: number }> = [];
  let earned = 0;
  let total = 0;
  let answered = 0;

  for (const q of qs) {
    const r = answers[q.id];
    const g = gradeQuestion(q, r);
    total += g.marksTotal;
    earned += g.marksEarned;
    if (r) answered++;
    const tag = q.tag ?? 'untagged';
    const t = (byTag[tag] ??= { earned: 0, total: 0, n: 0, right: 0 });
    t.total += g.marksTotal;
    t.earned += g.marksEarned;
    t.n++;
    if (g.correct) t.right++;
    if (r && !g.correct) wrong.push({ id: q.id, short: q.short ?? q.stem.slice(0, 30), tag: q.tag, marks: g.marksTotal, number: q.number });
    if (r?.seconds) timed.push({ id: q.id, short: q.short ?? q.stem.slice(0, 30), seconds: r.seconds });
  }

  const capped = Math.min(earned, total);
  // A run with only practice items still needs a percentage: fall back to
  // items right over items assessed so bands and the check comparison work.
  const pct = total > 0
    ? Math.round((capped / total) * 100)
    : qs.length > 0
      ? Math.round((qs.filter((q) => gradeQuestion(q, answers[q.id]).correct).length / qs.length) * 100)
      : 0;

  const summary: ReviseSummary = {
    earned: capped,
    total,
    pct,
    answered,
    assessed: qs.length,
    byTag,
    wrong,
    slow: timed.sort((a, b) => b.seconds - a.seconds).slice(0, 3),
    band: bandFor(run.results.bands, pct),
  };

  if (run.profile === 'paper' && run.board === 'nsc') {
    const levels = { L1: { earned: 0, total: 0 }, L2: { earned: 0, total: 0 }, L3: { earned: 0, total: 0 } };
    for (const q of qs) {
      const lv = nscLevelOf(q.tag);
      if (!lv) continue;
      const key = `L${lv}` as 'L1' | 'L2' | 'L3';
      const g = gradeQuestion(q, answers[q.id]);
      levels[key].total += g.marksTotal;
      levels[key].earned += g.marksEarned;
    }
    summary.levels = levels;
    summary.nscLevel = dbeLevel(pct);
  }

  return summary;
}

/** Marks total across assessed items, for the cover check and the validator. */
export function marksTotal(run: ReviseRun): number {
  return assessedQuestions(run).reduce((s, q) => s + marksOf(q), 0);
}

// ---------------------------------------------------------------------------
// Tracking payloads. One learning_interactions row per assessed item, keyed
// by question id, scored server-side against the registry answer key. The
// field names below are the ones `scoreResponse` already understands
// (optionId / found / order / placements) plus one added for `acceptAny`.
// ---------------------------------------------------------------------------

export type AnswerKeyValue =
  | string
  | string[]
  | { anyOf: string[] }
  /** The run summary row: the response carries its own 0–1 `score`; correctness is n/a. */
  | { selfScored: true }
  | Array<{ itemId: string; zoneId: string; position: number | null }>;

export function answerKeyFor(run: ReviseRun): Record<string, AnswerKeyValue> {
  const out: Record<string, AnswerKeyValue> = {};
  for (const q of assessedQuestions(run)) {
    switch (q.mode) {
      case 'mcq':
      case 'sentence-mark': {
        const acceptable = (Array.isArray(q.correct) ? q.correct : [q.correct ?? 0]).map(String);
        out[q.id] = acceptable.length === 1 ? acceptable[0] : { anyOf: acceptable };
        break;
      }
      case 'multi':
        out[q.id] = (Array.isArray(q.correct) ? q.correct : [q.correct ?? 0]).map(String);
        break;
      case 'order':
        out[q.id] = (q.sequence ?? []).map(String);
        break;
      case 'sort':
        out[q.id] = Object.entries(q.placement ?? {}).map(([itemId, zoneId]) => ({
          itemId,
          zoneId: String(zoneId),
          position: null,
        }));
        break;
      case 'build':
        break;
    }
  }
  for (const m of run.screens) {
    if (m.type === 'mta') out[m.id] = m.criteria.map((c) => c.correct);
  }
  out[REVISE_RESULTS_KEY] = { selfScored: true };
  return out;
}

export function questionPayload(q: QuestionScreen, r: QuestionResponse, g: Grade): Record<string, unknown> {
  const base: Record<string, unknown> = {
    questionId: q.id,
    mode: q.mode,
    tag: q.tag,
    correct: g.correct,
    marksEarned: g.marksEarned,
    marksTotal: g.marksTotal,
    seconds: r.seconds,
  };
  switch (q.mode) {
    case 'mcq':
    case 'sentence-mark':
      return { ...base, selected: r.selected, optionId: String(r.selected?.[0] ?? '') };
    case 'multi':
      return { ...base, selected: r.selected, found: (r.selected ?? []).map(String) };
    case 'order':
      return { ...base, sequence: r.sequence, order: (r.sequence ?? []).map(String) };
    case 'sort':
      return {
        ...base,
        placement: r.placement,
        placements: Object.entries(r.placement ?? {}).map(([itemId, zoneId]) => ({ itemId, zoneId: String(zoneId) })),
      };
    case 'build':
      return { ...base, selected: r.selected };
  }
}

export function mtaPayload(m: MtaScreen, r: MtaResponse): Record<string, unknown> {
  const g = gradeMta(m, r);
  return {
    questionId: m.id,
    mode: 'mta',
    verdicts: r.verdicts,
    order: m.criteria.map((c) => r.verdicts[c.id] ?? ''),
    matches: g.matches,
    of: g.of,
  };
}
